import { useEffect, useId, useRef, useState } from 'react';
import { normaliseIdentityField } from '../domain/identity';
import { t, tf } from '../i18n';
import './FilterBar.css';

/** A choice: its value, and the words shown when they differ (a country's name for its code). */
export type MultiSelectChoice = string | { value: string; label: string };

const valueOf = (choice: MultiSelectChoice) => (typeof choice === 'string' ? choice : choice.value);
const labelOf = (choice: MultiSelectChoice) => (typeof choice === 'string' ? choice : choice.label);

/**
 * A drop-down list where several values can be ticked (the Cépage filter, owner 2026-09-29; Pays,
 * Année and Note too, owner 2026-10-01): it looks like the other filter lists and shows the
 * ticked values in its field; the choices only appear once it is opened. Closes on a tap outside
 * or Escape. With `searchable` (owner, 2026-10-04) a search box at the top narrows the choices
 * (case and accents aside), without taking the focus when the list opens (owner, 2026-10-15:
 * ticking comes first); with `onAdd`, a search that matches no choice offers to add it.
 * Without `label`, a `<label for={buttonId}>` around it names it (a field edited in place).
 * With `listed` (owner, 2026-10-08: the region's grapes), only those values and the ticked ones are
 * listed until something is typed; the search still covers every choice.
 */
export function MultiSelectField({
  label,
  buttonId,
  choices,
  selected,
  onChange,
  emptyLabel,
  searchable = false,
  onAdd,
  listed,
}: {
  label?: string;
  buttonId?: string;
  choices: MultiSelectChoice[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Shown when nothing is ticked; "Tous" by default (a filter). */
  emptyLabel?: string;
  searchable?: boolean;
  onAdd?: (value: string) => void;
  /** The values listed before anything is typed; every choice when absent. */
  listed?: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
      if (event instanceof PointerEvent && root.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  function toggle(choice: string) {
    onChange(
      selected.includes(choice) ? selected.filter((v) => v !== choice) : [...selected, choice],
    );
  }

  // Ticked values in the list's order, or "Tous" when none is.
  const summary = choices.filter((choice) => selected.includes(valueOf(choice))).map(labelOf);
  const wanted = normaliseIdentityField(query);
  const shown =
    wanted !== ''
      ? choices.filter((choice) => normaliseIdentityField(labelOf(choice)).includes(wanted))
      : listed !== undefined
        ? choices.filter(
            (choice) => listed.includes(valueOf(choice)) || selected.includes(valueOf(choice)),
          )
        : choices;
  const canAdd =
    onAdd !== undefined &&
    wanted !== '' &&
    !choices.some((choice) => normaliseIdentityField(labelOf(choice)) === wanted);

  return (
    <div className="filter-bar__field multi-select" ref={root}>
      {label !== undefined && <span id={`${id}-label`}>{label}</span>}
      <button
        type="button"
        className="multi-select__button"
        {...(buttonId !== undefined ? { id: buttonId } : {})}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        {...(label !== undefined ? { 'aria-labelledby': `${id}-label ${id}-value` } : {})}
        onClick={() => setOpen(!open)}
      >
        <span id={`${id}-value`} className="multi-select__value">
          {summary.length > 0 ? summary.join(', ') : (emptyLabel ?? t('filters.any'))}
        </span>
      </button>
      {open && (
        <div
          id={`${id}-list`}
          className="multi-select__list"
          role="group"
          {...(label !== undefined ? { 'aria-labelledby': `${id}-label` } : {})}
        >
          {searchable && (
            <input
              type="search"
              className="multi-select__search"
              aria-label={t('filters.searchList')}
              placeholder={t('filters.searchList')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          )}
          {canAdd && (
            <button
              type="button"
              className="multi-select__add"
              onClick={() => {
                onAdd(query.trim().replace(/\s+/g, ' '));
                setQuery('');
              }}
            >
              {tf('filters.add', { value: query.trim() })}
            </button>
          )}
          {shown.map((choice) => (
            <label key={valueOf(choice)} className="multi-select__option">
              <input
                type="checkbox"
                checked={selected.includes(valueOf(choice))}
                onChange={() => toggle(valueOf(choice))}
              />
              {labelOf(choice)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
